declare global {
  interface Window {
    __PRERENDER__?: boolean;
    __PRERENDER_READY__?: boolean;
  }
}

window.__PRERENDER_READY__ = false;

/**
 * Global prerender fallback: if __PRERENDER__ and document complete, set __PRERENDER_READY__ after 2s.
 * Excluded for /oferta/* - job offer pages must set __PRERENDER_READY__ only when offer data is in DOM.
 */
if (
  window.__PRERENDER__ === true &&
  !window.location.pathname.startsWith('/oferta/')
) {
  const emitPrerenderReady = () => {
    setTimeout(() => {
      window.__PRERENDER_READY__ = true;
    }, 2000);
  };
  if (document.readyState === 'complete') {
    emitPrerenderReady();
  } else {
    window.addEventListener('load', emitPrerenderReady);
  }
}

/**
 * Workaround for React 18 + Firefox SVG bug.
 * React's getEventTarget() accesses `correspondingUseElement` on SVG elements,
 * which Firefox blocks with "Permission denied". We catch and suppress this
 * non-fatal error at the window level.
 */
window.addEventListener(
  'error',
  (e) => {
    if (e.message?.includes?.('correspondingUseElement')) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  },
  true,
);

import { SnackbarProvider } from 'notistack';
import React, { createRef, useEffect, useState, Suspense } from 'react';
// Apollo removed from main bootstrap - see BASELINE.md
// All views now use REST APIs (including Payment as of Iteration 5)
import { createRoot } from 'react-dom/client';
import { hot } from 'react-hot-loader';
import { Route, RouteComponentProps, Router, withRouter } from 'react-router-dom';
import { LastLocationProvider } from 'react-router-last-location';
import { QueryParamProvider } from 'use-query-params';

import { AnalyticsRouteTracker } from '@components/AnalyticsRouteTracker';
import { Maintenance } from '@components/Maintenance';
import { RootErrorHandler } from '@components/RootErrorHandler';
import { initAnalytics } from '@core/analytics';
import { DEV, TESTING, VERSION } from '@core/config';
import { history } from '@core/history';
import { snackbarConfig } from '@core/snacks';
import { useNetworkStatus } from '@hooks';
import { SiteLayout } from '@layouts';
import { AuthProvider } from '@providers/auth';
import { CredentialsProvider } from '@providers/credentials';
import { QueryProvider } from '@providers/query';
import { SiteProvider } from '@providers/site';
import { homeUrl, loginUrl, notFoundUrl, offerListingUrl, offlineUrl, registerUrl } from '@urls';

import PrerenderReset from '@components/PrerenderReset';
import { AppRouter } from './routers/App';

import { StylesProvider, ThemeProvider } from '@material-ui/core/styles';
import { theme } from '@styles';

window.version = VERSION;
window.testing = TESTING;

initAnalytics();

const CookieConsent = React.lazy(() =>
  import('@components/CookieConsent').then((m) => ({ default: m.CookieConsent })),
);

const CookieConsentAfterMount: React.FC = () => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;
  return (
    <Suspense fallback={null}>
      <CookieConsent />
    </Suspense>
  );
};

const Notifications: React.FC = () => {
  const { online } = useNetworkStatus();

  useEffect(() => {
    if (!online) {
      history.push(offlineUrl);
    }
  }, [online]);
  return null;
};

/** Ścieżki, które w serwisie mają trailing slash - canonical ma być ze slash. */
const CANONICAL_WITH_SLASH: Record<string, string> = {
  '/zaloguj': loginUrl,
  '/rejestracja': registerUrl,
};

const CanonicalLinkBase: React.FC<RouteComponentProps> = ({ location }) => {
  useEffect(() => {
    if (location.pathname === notFoundUrl) return;
    const pathname =
      CANONICAL_WITH_SLASH[location.pathname] ?? location.pathname;
    /** /oferty is alias: canonical always /. */
    const canonical =
      typeof window !== 'undefined'
        ? window.location.origin +
          (location.pathname === offerListingUrl ? homeUrl : pathname) +
          (location.pathname === offerListingUrl ? '' : (location.search || ''))
        : '';
    let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (link) {
      link.setAttribute('href', canonical);
    } else {
      link = document.createElement('link');
      link.rel = 'canonical';
      link.href = canonical;
      document.head.appendChild(link);
    }
  }, [location.pathname, location.search]);
  return null;
};
const CanonicalLink = withRouter(CanonicalLinkBase);

const startApp = async () => {
  const snackbarProviderRef = createRef();
  const RootComponent = () => (
    <Router history={history}>
      <PrerenderReset />
      <CanonicalLink />
      <AnalyticsRouteTracker />
      <QueryParamProvider ReactRouterRoute={Route}>
        <StylesProvider>
          <ThemeProvider theme={theme}>
            <RootErrorHandler>
              <LastLocationProvider>
                <QueryProvider>
                  <Maintenance>
                    {() => (
                      <>
                        <AuthProvider>
                          <CredentialsProvider>
                            <SiteProvider>
                              <SiteLayout>
                                <Notifications />
                                <AppRouter />
                                <CookieConsentAfterMount />
                              </SiteLayout>
                            </SiteProvider>
                          </CredentialsProvider>
                        </AuthProvider>
                      </>
                    )}
                  </Maintenance>
                </QueryProvider>
              </LastLocationProvider>
            </RootErrorHandler>
          </ThemeProvider>
        </StylesProvider>
      </QueryParamProvider>
    </Router>
  );
  const Root =
    typeof module !== 'undefined' && module.hot ? hot(module)(RootComponent) : RootComponent;

  const container = document.getElementById('root');
  const root = createRoot(container!);
  root.render(
    <SnackbarProvider ref={snackbarProviderRef} {...snackbarConfig(snackbarProviderRef)}>
      <Root />
    </SnackbarProvider>,
  );

  if (typeof module !== 'undefined' && module.hot) {
    module.hot.accept();
  }
};

startApp();
